import { CurrencyCode } from "@outschool/gql-backend-generated";
import {
  BASE_LOCALE,
  SupportedLocale,
  attachCurrencyDecoratorsToPrice
} from "@outschool/localization";
import { cleanUrlTerm } from "@outschool/routes";
import { joinCommasAnd, plural } from "@outschool/text";
import {
  DAY_END_HOUR,
  DAY_START_HOUR,
  briefDurationHoursString,
  briefDurationStringWithoutTimes,
  dayjs,
  formatAbsoluteTime,
  formatDayWithFullMonth,
  shortWeekdayRange,
  startDayShortString,
  timeOfDayToStartEndTimes
} from "@outschool/time";
import _ from "lodash";

import * as Activity from "./Activity";
import { RelativeDateFilter } from "./RelativeDateFilter";
import * as Routes from "./Routes";
import * as Section from "./Section";
import { isTermWithinSubjectList, niceSubjectTerm } from "./seo";

// Utility for manipulating a "filters" object, which represents ALL query parameters on the
// listings page.

const FILTER_CATCHALL_VALUES = {
  age: "All ages",
  theme: "All subjects",
  time: "Any time",
  timeOfDay: "Any time",
  format: "All formats",
  delivery: "Any length",
  startAfterTime: DAY_START_HOUR,
  endByTime: DAY_END_HOUR
};

const FILTER_DEFAULT_VALUES = {
  ...FILTER_CATCHALL_VALUES,
  order: "popular"
};

export const SAVED_ORDER_OPTION_DEFAULT = "saved";

const BRIEF_FORMAT_NAMES = {
  "Live online": "live",
  "Flex online": "flexible schedule",
  "Flexible schedule": "flexible schedule"
};

export const BRIEF_DELIVERY_NAMES = {
  "Live online": "live",
  "Flex online": "flexible schedule",
  "Flexible schedule": "flexible schedule",
  "Semester course": "semester",
  "Short course": "short",
  "One-time class": "one-time",
  "Ongoing class": "ongoing",
  "One-on-one class": "one-on-one",
  Camp: "camp",
  Group: "group"
};

export const PRICE_PER_MEETING_FILTERS = [
  "pricePerMeetingMax",
  "pricePerMeetingMin"
];

export const CAPACITY_FILTERS = ["capacityMax", "capacityMin"];

// These URL parameters are also used in Google Analytics to define "Site Search Settings".
// If you add/remove parameter names, please also update those settings in Google Analytics:
// Admin > View Settings > Site Search Settings > Query parameter & Category parameter.
// Do this once for each "view" (e.g. "All Web Site Data" and "Website: no admin traffic")
const FILTER_QUERY_PARAMETER_NAMES = [
  "age",
  "time",
  "timeOfDay",
  "theme",
  "delivery",
  "format",
  "startAfter",
  "startAfterTime",
  "endByTime",
  "endBy",
  "startBefore",
  "dow",
  "q",
  "order",
  "themeOrQuery",
  "enabledBooleanFilters",
  "languageOfInstruction",
  "multiTermQuery",
  "adminTags",
  "gradeLevel",
  "userUid",
  "userName",
  "originalSpelling",
  ...PRICE_PER_MEETING_FILTERS,
  ...CAPACITY_FILTERS
];

// Matches dayjs 'ddd' display format: https://day.js.org/docs/en/display/format
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
export const WEEKEND_DAYS = ["Sun", "Sat"];
// Our chosen display order
export const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dowIndex = (dow: string) => DAYS_OF_WEEK.indexOf(dow);
const sortDow = (daysOfWeek: string[]) =>
  daysOfWeek.sort((a, b) => dowIndex(a) - dowIndex(b));
export const formatDayOfWeek = (dow: string) =>
  dayjs().day(dowIndex(dow)).format("ddd");
export const formatDayOfWeekShort = (dow: string) =>
  dayjs().day(dowIndex(dow)).format("dd");

export const START_END_DATE_FORMAT = "YYYY-MM-DD";
const dateFormatRegex = new RegExp(/\d{4}-\d{2}-\d{2}/);
export function dateFilterToDayjs(dateFilterValue) {
  if (!dateFilterValue || !dateFormatRegex.test(dateFilterValue)) {
    return undefined;
  }
  const dateFilterDayjs = dayjs(dateFilterValue);
  if (dateFilterDayjs && dateFilterDayjs.isValid()) {
    return dateFilterDayjs;
  }
  return undefined;
}

export function displayTimeFromCardinalHour(cardinalHour) {
  const hour = Math.floor(cardinalHour);
  const minutes = Math.round((cardinalHour - Math.floor(cardinalHour)) * 60);
  return formatAbsoluteTime(hour, minutes);
}

export function formatSearchFiltersDate(date) {
  return !!date ? dayjs(date).format(START_END_DATE_FORMAT) : null;
}

// this hash stores a list of query terms that should be mapped to actual themes
// doubtless more sophisticated ways to handle this but it's a simple first pass
let queryToThemesHash = {};
// populate with full theme terms
Activity.Themes.forEach(e => (queryToThemesHash[Routes.slugifyTitle(e)] = e));
Activity.Themes.forEach(e => (queryToThemesHash[e.toLowerCase()] = e));

// add in other terms
const otherTerms = {
  health: "Health & Wellness",
  "study skills": "Life Skills",
  coding: "Coding & Tech",
  technology: "Coding & Tech",
  programming: "Coding & Tech",
  science: "Science & Nature",
  art: "Arts",
  ela: "English",
  "foreign language": "World Languages",
  "foreign languages": "World Languages"
};
Object.assign(queryToThemesHash, otherTerms);

// looks for the param themeOrQuery and maps it into either the
// theme or q parameter as appropriate
export function mapThemeOrQuery(query) {
  let queryCopy = Object.assign({}, query);

  if (queryCopy.themeOrQuery) {
    let theme = queryToThemesHash[queryCopy.themeOrQuery.toLowerCase()];
    if (theme) {
      queryCopy.theme = theme;
    } else {
      queryCopy.q =
        queryCopy.themeOrQuery + (queryCopy.q ? " " + queryCopy.q : "");
    }
    delete queryCopy.themeOrQuery;
  }

  if (queryCopy.theme && isTermWithinSubjectList(queryCopy.theme)) {
    queryCopy.theme = niceSubjectTerm(queryCopy.theme);
  }

  return queryCopy;
}

// used when a route populates themeOrQuery into a param
export function fromQueryAndParams(query, params) {
  if (params.themeOrQuery) {
    params.themeOrQuery = cleanSearchTerm(params.themeOrQuery);
    let queryCopy = Object.assign({}, query);
    queryCopy.themeOrQuery = params.themeOrQuery;

    return fromQuery(queryCopy);
  }
  return fromQuery(query);
}

export function isDateAfterOtherDates(date, otherDates) {
  return !otherDates
    .filter(otherDate => !!otherDate)
    .some(otherDate => date.isSameOrBefore(otherDate));
}

// TODO: memoize
export function fromQuery(query) {
  let filters = _.pick(query, FILTER_QUERY_PARAMETER_NAMES);
  filters = mapThemeOrQuery(filters);

  const now = dayjs().startOf("day");
  const startAfterDayjs = dateFilterToDayjs(filters.startAfter);
  const endByDayjs =
    filters.endBy !== catchallFor("endBy") && dateFilterToDayjs(filters.endBy);
  const startBeforeDayjs =
    filters.startBefore && dateFilterToDayjs(filters.startBefore);
  if (filters.multiTermQuery) {
    filters.multiTermQuery = filters.multiTermQuery.split(",");
  }
  if (
    !!endByDayjs &&
    !isDateAfterOtherDates(endByDayjs, [now, startAfterDayjs])
  ) {
    delete filters.endBy;
  }

  // delete startBefore if it is before startAfter and not the same date
  if (
    !!startBeforeDayjs &&
    !startBeforeDayjs.isSame(startAfterDayjs) &&
    !isDateAfterOtherDates(startBeforeDayjs, [now, startAfterDayjs])
  ) {
    delete filters.startBefore;
  }

  if (!!startAfterDayjs && !isDateAfterOtherDates(startAfterDayjs, [now])) {
    delete filters.startAfter;
  }

  if (!!filters.endBy && !!endByDayjs) {
    filters.endBy = endByDayjs.format(START_END_DATE_FORMAT);
  }
  if (!!filters.startBefore && !!startBeforeDayjs) {
    filters.startBefore = startBeforeDayjs.format(START_END_DATE_FORMAT);
  }
  if (!!filters.startAfter && !!startAfterDayjs) {
    filters.startAfter = startAfterDayjs.format(START_END_DATE_FORMAT);
  }
  const {
    startAfterTime,
    endByTime,
    pricePerMeetingMax,
    pricePerMeetingMin,
    capacityMax,
    capacityMin
  } = filters;
  if (startAfterTime) {
    filters.startAfterTime = parseFloat(startAfterTime);
  }
  if (endByTime) {
    filters.endByTime = parseFloat(endByTime);
  }
  if (pricePerMeetingMax) {
    filters.pricePerMeetingMax = parseInt(pricePerMeetingMax, 10);
  }
  if (pricePerMeetingMin) {
    filters.pricePerMeetingMin = parseInt(pricePerMeetingMin, 10);
  }
  if (capacityMax) {
    filters.capacityMax = parseInt(capacityMax, 10);
  }
  if (capacityMin) {
    filters.capacityMin = parseInt(capacityMin, 10);
  }
  return setDefaultSearch(filters);
}

export function catchallFor(filterName) {
  return FILTER_CATCHALL_VALUES[filterName];
}

export function defaultFor(filterName) {
  return FILTER_DEFAULT_VALUES[filterName];
}

export function setDefaultSearch(filters) {
  if (
    !filters.q ||
    typeof filters.q !== "string" ||
    !filters.q.trim() ||
    filters.q === "one on one"
  ) {
    delete filters.q;
  }

  return filters;
}

export function fromQueryAndValue(query, filterName, value) {
  let filters = fromQuery(query);
  if (value && value !== catchallFor(filterName)) {
    filters[filterName] = value;
  } else {
    delete filters[filterName];
  }

  return setDefaultSearch(filters);
}

// ie. Code & Tech --> coding-and-tech
export function convertThemeFilterToQuery(themes) {
  return cleanThemeParam(themes);
}

// ie. coding-and-tech --> Code & Tech
export function convertThemeQueryToFilter(themes) {
  return cleanThemeParam(themes, true);
}

export function cleanThemeParam(themes, returnNiceTerms = false) {
  return themes
    ?.split(",")
    .filter(t => isTermWithinSubjectList(t))
    .map(t => (!returnNiceTerms ? cleanUrlTerm(t) : niceSubjectTerm(t)))
    .join(",");
}

export function omitCatchalls(filters) {
  return _.omitBy(
    filters,
    (value, filterName) => value === catchallFor(filterName)
  );
}

// filters an array of listings
// accepts a string or array for ages
export function filterForAges(listings, ages) {
  var filteredListings = listings;
  if (ages && ages.length) {
    var ageList = ages;
    if (typeof ages === "string") {
      ageList = ageList.split(",").map(e => parseInt(e));
    }

    ageList = ageList.filter(age => age <= Activity.ageMax);

    filteredListings = listings.filter(listing =>
      ageList.reduce(function (prev, curr) {
        return prev || Activity.isAppropriateForAge(listing.activity, curr);
      }, false)
    );
  }

  return filteredListings;
}

export function filterForTime(listings, preferredTimeRanges, timeZone) {
  return listings.map(listing => {
    if (Activity.isFlexSchedule(listing.activity)) {
      return listing;
    }
    const filteredSections = listing.sections.filter(section =>
      // TODO: use meetings
      Section.matchesTimePreferences(
        listing.activity,
        section,
        preferredTimeRanges,
        timeZone,
        (listing.nextOngoingMeetingMap || {})[section.uid]
          ? listing.nextOngoingMeetingMap[section.uid]
          : null
      )
    );
    if (filteredSections.length === listing.sections.length) {
      return listing;
    }
    const filteredListing = _.omit(listing, ["sections"]);
    filteredListing.sections = filteredSections;
    return filteredListing;
  });
}

export function equal(filters1, filters2) {
  return (
    filters1 === filters2 ||
    FILTER_QUERY_PARAMETER_NAMES.every(
      param => filters1[param] + "" === filters2[param] + ""
    )
  );
}

function cleanSearchTerm(searchTerm: string): string {
  return (
    searchTerm
      // replace dashes with spaces
      ?.replace(/c-plus-plus/i, "c++")
      .replace(/c-sharp/i, "c#")
      .replace(/-and-/i, "-&-")
      .replace(/-/g, " ")
      .trim()
      .toLowerCase()
  );
}

function dowStringToArray(dowString) {
  return dowString
    .trim()
    .split(",")
    .map(d => d.trim())
    .filter(d => d !== "");
}

function dowAreConsecutive(dowArray) {
  const start = DAYS_OF_WEEK.indexOf(dowArray[0]);
  return _.isEqual(
    dowArray,
    _.slice(DAYS_OF_WEEK, start, start + dowArray.length)
  );
}

/**
 * @deprecated use: const { dowSummary } = useSearchFiltersString()
 */
export function dowSummary(dowString, shortDowSummary = true) {
  if (!dowString) {
    return null;
  }
  const daysOfWeek = sortDow(dowStringToArray(dowString));
  const daysOfWeekLabels = daysOfWeek.map(formatDayOfWeek);
  if (daysOfWeek.length === 1) {
    return daysOfWeekLabels[0];
  } else if (_.isEqual([...daysOfWeek].sort(), [...WEEKDAYS].sort())) {
    return "weekdays";
  } else if (_.isEqual([...daysOfWeek].sort(), [...WEEKEND_DAYS].sort())) {
    return "weekends";
  } else if (dowAreConsecutive(daysOfWeek)) {
    const lastDay = _.last(daysOfWeek);
    return shortWeekdayRange(
      dowIndex(daysOfWeek[0]),
      lastDay ? dowIndex(lastDay) : -1
    );
  } else {
    if (!shortDowSummary) {
      return daysOfWeekLabels.join(", ");
    }
    return "some days";
  }
}

function startEndTimeSummary(startAfterTime, endByTime): string {
  if (startAfterTime === DAY_START_HOUR) {
    startAfterTime = undefined;
  }
  if (endByTime === DAY_END_HOUR) {
    endByTime = undefined;
  }
  if (startAfterTime && endByTime) {
    return `between ${displayTimeFromCardinalHour(
      startAfterTime
    )} and ${displayTimeFromCardinalHour(endByTime)}`;
  } else if (startAfterTime && !endByTime) {
    return `after ${displayTimeFromCardinalHour(startAfterTime)}`;
  } else if (endByTime && !startAfterTime) {
    return `before ${displayTimeFromCardinalHour(endByTime)}`;
  } else {
    return "";
  }
}

export function gradeLevelArrayToString(gradeLevels: string[]) {
  return sortByGradeLevel(gradeLevels).join(", ");
}

export function agesArrayToString(ages) {
  return _.sortBy(ages.map(a => parseInt(a)))
    .reduce<number[][]>((ageRanges, age) => {
      const ageRange = _.last(ageRanges);
      const lastAgeInRange = ageRange && _.last(ageRange);
      if (_.isNumber(lastAgeInRange) && age - lastAgeInRange <= 1) {
        ageRanges[ageRanges.length - 1] = [_.head(ageRange), age];
        return ageRanges;
      } else {
        return [...ageRanges, [age]];
      }
    }, [])
    .map(ageRange => (ageRange.length === 1 ? ageRange : ageRange.join("-")))
    .join(", ");
}

function dayRangeToString(startAfter, endBy): string | undefined {
  const startAfterDayjs = dateFilterToDayjs(startAfter);
  const endByDayjs = dateFilterToDayjs(endBy);

  const startAfterDateString = startDayShortString(startAfter);
  const endByDateString = startDayShortString(endBy);

  if (startAfterDayjs && !endByDayjs) {
    return `from ${startAfterDateString}`;
  } else if (!startAfterDayjs && endByDayjs) {
    return `until ${endByDateString}`;
  } else if (startAfterDayjs && endByDayjs) {
    if (startAfterDayjs.isSame(endByDayjs, "day")) {
      return startAfterDateString;
    } else {
      return briefDurationStringWithoutTimes(startAfter, endBy);
    }
  } else {
    return undefined;
  }
}

export function timeRangeToString(startAfterTime, endByTime) {
  if (!startAfterTime && !endByTime) {
    return null;
  }

  const startTime = startAfterTime || DAY_START_HOUR;
  const endTime = endByTime || DAY_END_HOUR;

  return briefDurationHoursString(startTime, endTime);
}

export interface ScheduleFiltersToStringParams {
  dow?: string | null;
  startAfter?: string | null;
  startBefore?: string | null;
  endBy?: string | null;
  startAfterTime?: number | null;
  endByTime?: number | null;
  shortDowSummary?: boolean;
}
export function scheduleFiltersToString({
  dow,
  startAfter,
  startBefore,
  endBy,
  startAfterTime,
  endByTime,
  shortDowSummary = true
}: ScheduleFiltersToStringParams = {}): string {
  const dowString = dowSummary(dow, shortDowSummary) || "";
  const dayRangeString =
    dayRangeToString(startAfter, endBy || startBefore) || "";
  const timeRangeString = timeRangeToString(startAfterTime, endByTime) || "";

  return _.upperFirst(
    _.filter([
      `${dowString}${dowString && dayRangeString ? ", " : ""}${dayRangeString}`,
      timeRangeString
    ]).join(" ")
  );
}

export function summarySentence(filters, includeOrder = false): string {
  const orderString = (() => {
    switch (filters.order) {
      case "new":
        return "New classes first.";
      case "upcoming":
        return "Upcoming classes first.";
      case "popular":
      default:
        return "Popular classes first.";
    }
  })();

  if (_.isEmpty(_.omit(filters, "order"))) {
    return orderString;
  }

  const educator = filters.userName ? `taught by ${filters.userName}` : "";

  const formatList = filters.delivery?.split(",");
  const format =
    formatList?.length === 1 ? BRIEF_FORMAT_NAMES[formatList[0]] : "";
  const deliveryList = filters.delivery?.split(",");
  const delivery = joinCommasAnd(
    deliveryList?.map(d => BRIEF_DELIVERY_NAMES[d])
  );

  const theme =
    filters.theme && filters.theme !== catchallFor("theme")
      ? "about " + joinCommasAnd(` ${filters.theme}`.split(","))?.toLowerCase()
      : null;
  if (filters.timeOfDay) {
    const { startAfterTime, endByTime } = timeOfDayToStartEndTimes(
      filters.timeOfDay
    );
    filters.startAfterTime = startAfterTime;
    filters.endByTime = endByTime;
  }
  const startEndTime = startEndTimeSummary(
    filters.startAfterTime,
    filters.endByTime
  );
  const ages =
    filters.age && filters.age !== catchallFor("age") && filters.age.split(",");
  const age = ages
    ? ` for ${plural("age", ages.length)} ${agesArrayToString(ages)}`
    : null;

  const gradeLevels = filters.gradeLevel && filters.gradeLevel.split(",");
  const gradeLevel = gradeLevels
    ? ` in ${gradeLevelArrayToString(gradeLevels)}`
    : null;

  const relativeDateFilter = _.lowerFirst(
    relativeDateFilterToString(filters.startAfter)
  );
  const startAfterDayjs = dateFilterToDayjs(filters.startAfter);
  const endByDayjs = dateFilterToDayjs(filters.endBy);
  const dow = dowSummary(filters.dow);
  const query = filters.q ? ` containing "${filters.q}"` : null;

  const startAfterDateString = formatDayWithFullMonth(startAfterDayjs);
  const endByDateString = formatDayWithFullMonth(endByDayjs);

  let classSize = "";
  const { capacityMax, capacityMin } = filters;
  if (capacityMin && capacityMax) {
    classSize = `for ${capacityMin}}-${capacityMax}} learners`;
  } else if (capacityMin) {
    classSize = `for ${capacityMin}+ learners`;
  } else if (capacityMax === 1) {
    classSize = "for 1 learner";
  } else if (capacityMax) {
    classSize = `for up to ${capacityMax} learners`;
  }

  const filterString =
    [
      delivery,
      format,
      "classes",
      educator,
      theme,
      startEndTime,
      age,
      gradeLevel,
      relativeDateFilter,
      startAfterDayjs
        ? ` ${endByDayjs ? "from" : "after"} ${startAfterDateString}`
        : "",
      endByDayjs ? ` through ${endByDateString}` : "",
      dow ? " on " + dow : null,
      classSize,
      query
    ]
      .filter(n => Boolean(n))
      .join(" ")
      .trim() + ".";

  return (
    filterString.charAt(0).toUpperCase() +
    filterString.slice(1) +
    (includeOrder ? " " + orderString : "")
  );
}

export function hasNonOrderFilters(filters) {
  const keys = Object.keys(filters);
  return keys.length && (keys.length > 1 || keys[0] !== "order");
}

/**
 * @deprecated use: const translateRelativeDateFilter = useTranslateRelativeDateFilter()
 */
export function relativeDateFilterToString(relativeDateFilter) {
  return relativeDateFilter === RelativeDateFilter.ThisWeek
    ? "Starting this week or later"
    : relativeDateFilter === RelativeDateFilter.NextWeek
    ? "Starting next week or later"
    : relativeDateFilter === RelativeDateFilter.NextMonth
    ? "Starting next month or later"
    : relativeDateFilter === RelativeDateFilter.Today
    ? "Today"
    : relativeDateFilter === RelativeDateFilter.SevenDays
    ? "Next 7 days"
    : relativeDateFilter === RelativeDateFilter.FourteenDays
    ? "Next 14 days"
    : relativeDateFilter === RelativeDateFilter.AllUpcoming
    ? "All upcoming dates"
    : "";
}

export function pricePerMeetingToString({
  pricePerMeetingMax,
  pricePerMeetingMin,
  currencyCode,
  convert = amount => amount,
  roundUp,
  locale = BASE_LOCALE
}: {
  pricePerMeetingMax: number;
  pricePerMeetingMin: number;
  currencyCode: CurrencyCode;
  convert: (number) => number;
  roundUp: boolean;
  locale: SupportedLocale;
}) {
  const pricePerMeetingMaxDisplay = attachCurrencyDecoratorsToPrice({
    price: convert(pricePerMeetingMax),
    showCurrencyCode: false,
    currencyCode: currencyCode,
    displayNoCents: true,
    roundUp,
    locale
  });
  const pricePerMeetingMinDisplay = attachCurrencyDecoratorsToPrice({
    price: convert(pricePerMeetingMin),
    showCurrencyCode: false,
    currencyCode: currencyCode,
    displayNoCents: true,
    roundUp,
    locale
  });

  return pricePerMeetingMax
    ? `${pricePerMeetingMaxDisplay} or less`
    : pricePerMeetingMin
    ? `More than ${pricePerMeetingMinDisplay}`
    : "Any";
}

// filters currently set from the query textbox
export const QUERY_FILTERS = ["q", "teacherName", "teacherUid"];

export const RELATIVE_DATE_FILTERS = [
  RelativeDateFilter.Today,
  RelativeDateFilter.SevenDays,
  RelativeDateFilter.FourteenDays,
  RelativeDateFilter.Summer,
  RelativeDateFilter.Fall,
  RelativeDateFilter.January,
  RelativeDateFilter.AllUpcoming
];

export const DATE_FILTERS = ["startAfter", "endBy", "startBefore"];

export const TIME_FILTERS = ["startAfterTime", "endByTime", "dow"];

export const SCHEDULE_FILTERS = [...DATE_FILTERS, ...TIME_FILTERS];

export const PRICE_PER_MEETING_OPTIONS = [
  {},
  { pricePerMeetingMax: 10 },
  { pricePerMeetingMax: 20 },
  { pricePerMeetingMax: 30 },
  { pricePerMeetingMax: 40 },
  { pricePerMeetingMin: 40 }
];

export const CAPACITY_OPTIONS = [{}, { capacityMax: 1 }, { capacityMin: 2 }];

function sortByGradeLevel(gradeLevelList: string[]) {
  const sortedGradeLevel = gradeLevelList.sort((a, b) => {
    const gradeLevelNumberA = parseInt(a);
    const gradeLevelNumberB = parseInt(b);

    if (isNaN(gradeLevelNumberA)) {
      return -1;
    } else if (isNaN(gradeLevelNumberB)) {
      return 1;
    } else if (gradeLevelNumberA < gradeLevelNumberB) {
      return -1;
    } else if (gradeLevelNumberA > gradeLevelNumberB) {
      return 1;
    } else {
      return 0;
    }
  });

  return sortedGradeLevel;
}
